import axios, { type AxiosInstance } from 'axios'

import communityMediaDefaultService from '@/services/community-media-default.service'
import { getEdunodeServerBaseUrl, getServerApiKey } from '@/services/edunode-api-helpers'
import type { TenantConfig, TenantCustomization } from '@/services/tenant.service'
import { isCommunityPubliclyAccessible } from '@/utils/community-status.utils'

export interface CommunityConfigResponse {
  id: string
  name: string
  subdomain: string
  selectedTemplateId: string | null
  templateSelectedAt?: string | null
  [key: string]: unknown
}

class CommunityConfigService {
  private static instance: CommunityConfigService
  private readonly client: AxiosInstance

  private constructor() {
    const isServer = typeof window === 'undefined'
    const apiKey = isServer ? getServerApiKey() : ''
    const baseURL = isServer ? getEdunodeServerBaseUrl() : '/api/edunode'

    this.client = axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(apiKey ? { 'x-api-key': apiKey } : {}),
      },
    })
  }

  public static getInstance(): CommunityConfigService {
    if (!CommunityConfigService.instance) {
      CommunityConfigService.instance = new CommunityConfigService()
    }
    return CommunityConfigService.instance
  }

  async getBySubdomain(subdomain: string): Promise<CommunityConfigResponse | null> {
    if (!subdomain || subdomain === 'default') {
      return null
    }

    const url = `/communities/subdomain/${encodeURIComponent(subdomain)}`
    try {
      const response = await this.client.get<CommunityConfigResponse>(url)
      const community = response.data
      if (!community || !isCommunityPubliclyAccessible(String(community.status ?? ''))) {
        return null
      }
      return community
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null
      }
      throw err instanceof Error ? err : new Error('Failed to load community config')
    }
  }

  /**
   * Build tenant config for community hosts from eztech-be only (no fsds-be media defaults).
   */
  async getTenantConfigBySubdomain(subdomain: string): Promise<TenantConfig | null> {
    const community = await this.getBySubdomain(subdomain)
    if (!community) {
      return null
    }

    let mediaDefaults: Awaited<ReturnType<typeof communityMediaDefaultService.getByCommunityId>> | null = null
    try {
      mediaDefaults = await communityMediaDefaultService.getByCommunityId(community.id)
    } catch {
      // Community config may already include merged mediaDefaults from eztech-be.
    }
    const customization = communityMediaDefaultService.mergeIntoCustomization(
      community.customization as TenantCustomization | undefined,
      mediaDefaults
    )

    return {
      id: community.id,
      name: community.name,
      subdomain: community.subdomain,
      theme: communityMediaDefaultService.mergeThemeWithMediaDefaults(
        (community.theme as TenantConfig['theme']) ?? {},
        mediaDefaults
      ),
      customization,
      features: Array.isArray(community.features) ? (community.features as string[]) : [],
      status: typeof community.status === 'string' ? community.status : 'ACTIVE',
    }
  }

  async getByDomain(domain: string): Promise<CommunityConfigResponse | null> {
    if (!domain) {
      return null
    }

    const url = `/communities/domain/${encodeURIComponent(domain)}`
    try {
      const response = await this.client.get<CommunityConfigResponse>(url)
      const community = response.data
      if (!community || !isCommunityPubliclyAccessible(String(community.status ?? ''))) {
        return null
      }
      return community
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        return null
      }
      throw err instanceof Error ? err : new Error('Failed to load community config')
    }
  }
}

export const communityConfigService = CommunityConfigService.getInstance()
